// SPDX-License-Identifier: AGPL-3.0-or-later
// KONVO: the shared contract for Assistant engagement: activity tracking, the
// corner assistant (meeting prep pop-ups and suggestions), starting points,
// connect invitations, getting started, nudges from Konvo and the staff-only
// Team insights page. The API and the app both import these shapes.
// See docs/konvo/assistant-engagement.md.

import {z} from 'zod';
import {ClientDeviceSchema} from './ClientVersion';

// ---------------------------------------------------------------------------
// Areas, starting points, stages
// ---------------------------------------------------------------------------

/** The parts of Konvo whose use is measured (time in front, days used). */
export const ACTIVITY_AREAS = [
	'chats',
	'mail',
	'calendar',
	'tasks',
	'whatsapp',
	'home',
	'notes',
	'calls',
	'assistant',
	'agents',
	'insights',
	'settings',
	'other',
] as const;
export type ActivityArea = (typeof ACTIVITY_AREAS)[number];

/** The suggested requests shown where people work, and what the corner suggestions run. */
export const STARTERS = [
	'client_needs', // Mail: "Find what <client> still needs"
	'task_from_email', // Mail: "Create a task from this email"
	'draft_reply', // Mail: "Draft a reply"
	'outstanding_work', // Tasks: "Find my outstanding work"
	'review_time', // Tasks: "Review my time"
	'meeting_prep', // Calendar: "Prepare me for this meeting"
	'free', // typed by the user in the panel or ⌘K "Ask Konvo"
] as const;
export type Starter = (typeof STARTERS)[number];

/** Where a person is on the way to using the Assistant every week. */
export const ASSISTANT_STAGES = ['not_connected', 'connected_unused', 'trying', 'active'] as const;
export type AssistantStage = (typeof ASSISTANT_STAGES)[number];

// ---------------------------------------------------------------------------
// Activity events (POST /konvo/activity/events)
// ---------------------------------------------------------------------------

/**
 * Every event the app reports. Only what happened is stored, never what was
 * written: no message, mail, note or search text.
 */
export const ACTIVITY_EVENT_TYPES = [
	// Using Konvo
	'area.visit', // {area, seconds, started_at}  one closed visit with time in front
	'action.done', // {area, action: reply|send|task.create|time.log|event.create|mail.send|..., origin: konvo|bridged}
	// Starting points and the Konvo panel
	'starter.shown', // {starter, page}  once per page view, not per render
	'starter.clicked', // {starter, page, locked, meeting_id?, suggestion_id?}  ids only, no free-text context
	'panel.opened', // {starter, page, source: starter|suggestion|ask|nudge|checklist|dm_card, meeting_id?, suggestion_id?, nudge_id?}
	'panel.request_edited', // {starter}
	'panel.result', // {starter, ms, ok}
	'panel.action_used', // {starter, action: task|reply|assign|done|open_source|log_time|notes|...}
	'panel.followup', // {starter}
	'panel.to_dm', // {starter}
	'panel.closed', // {starter, seconds}
	// Corner suggestions (one suggestion_id from planned to followed)
	'suggestion.shown', // {suggestion_id, kind, page, device}
	'suggestion.held_back', // {suggestion_id, kind, reason: in_call|typing|quiet_hours|limit|screen_share|hidden_tab}
	'suggestion.seen', // {suggestion_id, kind, seconds}
	'suggestion.hovered', // {suggestion_id, kind}
	'suggestion.clicked', // {suggestion_id, kind, target: main|later|x|menu:snooze|menu:not_this|menu:stop|menu:settings}
	'suggestion.closed', // {suggestion_id, kind, how: x|timeout|navigated|later|main}
	'suggestion.followed', // {suggestion_id, kind, next: same_opened|stayed|left|asked_konvo}
	// Connecting the assistant
	'connect.step', // {step: opened|provider_chosen|sign_in_opened|code_pasted|connected|closed, provider?, source}
	// Getting started, DM starter cards
	'checklist.item_done', // {item}
	'checklist.dismissed', // {}
	'dm_starters.shown', // {}
	'dm_starters.clicked', // {starter}
	// Nudges from Konvo
	'nudge.read', // {nudge_id}
	'nudge.connect_clicked', // {nudge_id}
	'nudge.try_clicked', // {nudge_id, starter}
	// Feedback asks from Konvo (AppFeedback.ts)
	'feedback.read', // {ask_id}
] as const;
export type ActivityEventType = (typeof ACTIVITY_EVENT_TYPES)[number];

export const DEVICE_KINDS = ['desktop', 'web', 'phone'] as const;
export type DeviceKind = (typeof DEVICE_KINDS)[number];

/** Strings up to 400 so Outlook event ids (~150+ characters) fit; the server shortens non-id strings to 120. */
const PropValue = z.union([z.string().max(400), z.number(), z.boolean(), z.null()]);

export const ActivityEventSchema = z.object({
	type: z.enum(ACTIVITY_EVENT_TYPES),
	/** ISO time on the client; the server clamps it to the last 24 h. */
	at: z.string().max(40),
	device: z.enum(DEVICE_KINDS).default('web'),
	/** Small flat properties, see the comments on ACTIVITY_EVENT_TYPES. */
	props: z.record(z.string().max(40), PropValue).default({}),
});
export type ActivityEvent = z.infer<typeof ActivityEventSchema>;

export const ActivityEventBatchSchema = z.object({
	events: z.array(ActivityEventSchema).min(1).max(100),
});
export type ActivityEventBatch = z.infer<typeof ActivityEventBatchSchema>;

// ---------------------------------------------------------------------------
// Corner assistant: suggestions and settings
// ---------------------------------------------------------------------------

/**
 * `meeting_prep` has no daily limit (one per qualifying meeting). Every other
 * kind is a suggestion and counts toward the per-day limit.
 */
export const CORNER_KINDS = [
	'meeting_prep',
	'client_followup',
	'outstanding_work',
	'review_time',
	'promise_due',
	'connect_invite',
	/**
	 * An agent chat finished without a ClickUp task and Konvo was not sure which (once per chat;
	 * design/agents-task-linking E1). `action.context` names the chat: agent_machine, agent_thread,
	 * agent_chat (its title). Apps before it drop the suggestion unread.
	 */
	'agent_task',
	/**
	 * "Waiting on a reply" (design/waiting-replies A): someone is still waiting on the person's
	 * answer, or the person on someone else's. `action.special` is 'open_waiting' and
	 * `action.context.item_id` names the item. waiting_on_you replaces client_followup, which is
	 * no longer planned (old rows keep the kind).
	 */
	'waiting_on_you',
	'waiting_on_them',
	/**
	 * The end-of-day wrap-up (design/evening-wrapup r2): the same plan card as in the Konvo chat.
	 * `action.special` is 'open_wrap_up' and `action.context.plan_id` names the plan; channel_id and
	 * message_id point at its message.
	 */
	'wrap_up',
] as const;
export type CornerKind = (typeof CORNER_KINDS)[number];

/** Kinds added after the first apps: lists for older apps (recent, insights) leave them out. */
export const CORNER_KINDS_LATER: ReadonlyArray<CornerKind> = [
	'agent_task',
	'waiting_on_you',
	'waiting_on_them',
	'wrap_up',
];

export const CORNER_STATUSES = [
	'planned', // on the schedule, not yet due
	'held', // due, waiting for the person to be free (up to 10 min)
	'shown', // on screen (or pushed)
	'used', // main action taken
	'later', // snoozed for 30 min, comes back once
	'dismissed', // ✕ or the menu
	'timed_out', // closed by itself after 20 s
	'skipped', // cancelled before showing (brief opened already, meeting moved, staff skipped, limit)
	'expired', // never shown in time
] as const;
export type CornerStatus = (typeof CORNER_STATUSES)[number];

/** What the pop-up's main button does. */
export const CornerActionSchema = z.object({
	/** Opens the Konvo panel with this starting point and runs it. */
	starter: z.enum(STARTERS).optional(),
	/** Prefilled request text for the panel (editable there). */
	request: z.string().max(1000).optional(),
	/** Context the request is about (a meeting, a sender, a thread, a task). */
	context: z.record(z.string().max(40), z.string().max(400)).optional(),
	/** Special actions that are not a panel run. */
	special: z.enum(['connect', 'open_brief', 'open_waiting', 'open_wrap_up']).optional(),
	/** Existing engine brief message to open instead (special = open_brief). */
	channel_id: z.string().max(40).optional(),
	message_id: z.string().max(40).optional(),
});
export type CornerAction = z.infer<typeof CornerActionSchema>;

/** Numbers for the connect invitation, worked out without the model. */
export const TeaserSchema = z.object({
	unread_client_mail: z.number().int().min(0),
	client_names: z.array(z.string().max(80)).max(5),
	outside_meetings_tomorrow: z.number().int().min(0),
	meeting_names: z.array(z.string().max(120)).max(5),
	tasks_due_week: z.number().int().min(0),
	tasks_overdue: z.number().int().min(0),
	/** Personal "things to try" built from the same numbers (for nudges, checklists, DM starters). */
	tries: z
		.array(
			z.object({
				starter: z.enum(STARTERS),
				title: z.string().max(120),
				reason: z.string().max(160),
				request: z.string().max(1000),
				context: z.record(z.string().max(40), z.string().max(400)).optional(),
			}),
		)
		.max(5),
});
export type Teaser = z.infer<typeof TeaserSchema>;

// ---------------------------------------------------------------------------
// Ready cards: a pop-up that arrives with the work done (design/suggestion-stories)
// ---------------------------------------------------------------------------

/** The icon in front of one line of a ready card. */
export const READY_LINE_ICONS = [
	'mail',
	'calendar',
	'task',
	'promise',
	'file',
	'person',
	'time',
	'agent',
	'chat',
] as const;
export type ReadyLineIcon = (typeof READY_LINE_ICONS)[number];

/** One fact on the card: "September activation numbers per store" / "For the board pack, closes Friday". */
export const ReadyLineSchema = z.object({
	icon: z.enum(READY_LINE_ICONS),
	title: z.string().max(160),
	detail: z.string().max(200).optional(),
	/** ok: good news (free, done); warn: needs attention (not answered, overdue). */
	tone: z.enum(['ok', 'warn']).optional(),
});
export type ReadyLine = z.infer<typeof ReadyLineSchema>;

/** A reply Konvo already wrote (client follow-up): sent as a reply to `mail_id`. */
export const ReadyReplySchema = z.object({
	/** Graph message id of the mail being answered. */
	mail_id: z.string().max(400),
	/** Plain text, times already filled in from the calendars. */
	body: z.string().max(2000),
	/** Who it goes to, for the card ("To Danie Coetzee"). */
	to_name: z.string().max(120).optional(),
});
export type ReadyReply = z.infer<typeof ReadyReplySchema>;

/**
 * What a ready card's buttons do. The first action is the main button.
 *   send_reply    sends `reply` as is (the app offers Undo for a few seconds)
 *   open_mail     opens `mail_id` in Mail with Konvo's card on it (to edit the reply, pick another time)
 *   add_event     opens the quick event card with `event`
 *   mail_task     opens the task form for `mail_id` with `task_name`
 *   promise_done / promise_drop   POST /konvo/scheduled/promises/:commitment_id
 *   open_brief    opens the engine's brief (`channel_id`/`message_id`), else the Assistant chat
 *   join          opens `url` (Teams/Meet link)
 *   open_dm       opens the Assistant chat
 *   run           what the pop-up did before ready cards: the suggestion's own `action` (panel run, connect)
 */
export const READY_ACTION_KINDS = [
	'send_reply',
	'open_mail',
	'add_event',
	'mail_task',
	'promise_done',
	'promise_drop',
	'open_brief',
	'join',
	'open_dm',
	'run',
] as const;
export type ReadyActionKind = (typeof READY_ACTION_KINDS)[number];

export const ReadyActionSchema = z.object({
	kind: z.enum(READY_ACTION_KINDS),
	label: z.string().max(40),
	mail_id: z.string().max(400).optional(),
	commitment_id: z.string().max(400).optional(),
	channel_id: z.string().max(40).optional(),
	message_id: z.string().max(40).optional(),
	url: z.string().max(1000).optional(),
	task_name: z.string().max(160).optional(),
	event: z
		.object({
			title: z.string().max(160),
			start: z.string().max(40),
			end: z.string().max(40),
		})
		.optional(),
});
export type ReadyAction = z.infer<typeof ReadyActionSchema>;

/** The finished card: who/what, the facts, a written reply when there is one, and the buttons. */
export const ReadyCardSchema = z.object({
	/** Top line after "Konvo ·": "Danie Coetzee, iCAM", "in 20 minutes". */
	context: z.string().max(80).optional(),
	/** "Danie needs two things from you". */
	headline: z.string().max(160),
	lines: z.array(ReadyLineSchema).max(3),
	reply: ReadyReplySchema.optional(),
	actions: z.array(ReadyActionSchema).min(1).max(3),
	/** Quiet footer: why now, or where it came from. */
	note: z.string().max(240).optional(),
	prepared_at: z.string().max(40),
});
export type ReadyCard = z.infer<typeof ReadyCardSchema>;

/** One pop-up as the app receives it (GET /konvo/corner/pending, gateway KONVO_CORNER_UPDATE). */
export const CornerSuggestionSchema = z.object({
	id: z.string().max(40),
	kind: z.enum(CORNER_KINDS),
	status: z.enum(CORNER_STATUSES),
	/** When it should appear (meeting start minus the lead time for meeting prep). */
	due_at: z.string().max(40),
	/** Drop it if it has not been shown by then. */
	expires_at: z.string().max(40),
	/** One sentence written for this moment. */
	title: z.string().max(160),
	/** Why now: what the assistant noticed. Required: no reason, no pop-up. */
	reason: z.string().max(240),
	/** Main button label, e.g. "Prepare me". */
	primary_label: z.string().max(40),
	action: CornerActionSchema,
	/** For connect invitations: the numbers shown as rows. */
	teaser: TeaserSchema.optional(),
	/** Meeting prep: the meeting it is for (so Calendar can skip if the brief was opened). */
	meeting: z
		.object({
			id: z.string().max(400),
			subject: z.string().max(200),
			start: z.string().max(40),
			attendees: z.array(z.string().max(120)).max(12),
		})
		.optional(),
	/** Also send a phone push if no app showed it within a minute (meeting prep, promises). */
	push_when_closed: z.boolean().default(false),
	/**
	 * Meeting prep only: the last three were ignored, so this pop-up also asks once whether to
	 * keep meeting prep ("Keep them" = outcome used, "Turn off" = dismissed with menu 'stop').
	 */
	ask_keep: z.boolean().optional(),
	/** The work already done (ready card). Without it the app shows title, reason and the main button. */
	ready: ReadyCardSchema.optional(),
});
export type CornerSuggestion = z.infer<typeof CornerSuggestionSchema>;

/**
 * One entry in the "From Konvo" list (GET /konvo/corner/list): a corner suggestion or an engine
 * brief/nudge from today, as one shape. Entries stay until done, dismissed or no longer true
 * (replied, promise kept, meeting started); a pop-up that closed by itself folds into the list.
 */
export const ReadyItemSchema = z.object({
	/** `c:<suggestion id>` or `e:<engine item id>`. */
	id: z.string().max(60),
	source: z.enum(['corner', 'engine']),
	/** Corner kind (client_followup, meeting_prep, …) or engine trigger (vip_mail, commitment_due, …). */
	kind: z.string().max(40),
	/** When it became ready (shown, due or created). */
	at: z.string().max(40),
	state: z.enum(['ready', 'done']),
	/** Always present: corner rows without a ready card get one built from title, reason and main button. */
	card: ReadyCardSchema,
	/** Corner entries: the suggestion itself (outcomes, `run`, the agent_task card). */
	suggestion: CornerSuggestionSchema.optional(),
	/** Engine entries: the item id for POST /konvo/assistant/engine/items/:id/outcome. */
	engine_item_id: z.string().max(40).optional(),
	/** Done entries: what happened ("Sent", "Replied in Outlook", "Kept"). */
	done_note: z.string().max(80).optional(),
});
export type ReadyItem = z.infer<typeof ReadyItemSchema>;

export const ReadyListResponseSchema = z.object({
	/** Ready for you: newest first, meeting prep for meetings still to come first of all. */
	items: z.array(ReadyItemSchema).max(20),
	/** Done today: newest first. */
	done: z.array(ReadyItemSchema).max(10),
	/** "Am I getting in your way?" is waiting to be asked (as on CornerPendingResponse). Absent for older servers. */
	ask_change: z.boolean().optional(),
	/** How many pop-ups in a row were closed (the number the ask names). */
	ask_change_closed: z.number().int().min(0).optional(),
});
export type ReadyListResponse = z.infer<typeof ReadyListResponseSchema>;

export const CornerPendingResponseSchema = z.object({
	suggestions: z.array(CornerSuggestionSchema),
	/** Today's suggestion count against the limit (meeting prep not counted). */
	used_today: z.number().int().min(0),
	limit: z.number().int().min(0),
	stage: z.enum(ASSISTANT_STAGES),
	/**
	 * Konvo's pop-ups were closed 5 times in a row (any kind): the app asks once "Am I getting in your
	 * way?" (POST /konvo/corner/ask-change). Older apps ignore it.
	 */
	ask_change: z.boolean().default(false),
	/** How many pop-ups in a row were closed (the number the ask names). */
	ask_change_closed: z.number().int().min(0).optional(),
});
export type CornerPendingResponse = z.infer<typeof CornerPendingResponseSchema>;

/** POST /konvo/corner/suggestions/:id/outcome */
export const CornerOutcomeSchema = z.object({
	status: z.enum(['shown', 'held', 'used', 'later', 'dismissed', 'timed_out']),
	/** For dismissals from the menu. */
	menu: z.enum(['snooze', 'not_this', 'stop', 'settings']).optional(),
	page: z.string().max(40).optional(),
	device: z.enum(DEVICE_KINDS).optional(),
});
export type CornerOutcome = z.infer<typeof CornerOutcomeSchema>;

export const CORNER_PER_DAY_CHOICES = [1, 2, 4] as const;

/** on: suggestions pop up; list_only: nothing pops up, everything still goes into the From Konvo list. */
export const CORNER_POPUP_MODES = ['on', 'list_only'] as const;
export type CornerPopupMode = (typeof CORNER_POPUP_MODES)[number];

export const CornerSettingsSchema = z.object({
	enabled: z.boolean().default(true),
	/** Suggestions a day (meeting prep is not counted). */
	per_day: z.union([z.literal(1), z.literal(2), z.literal(4)]).default(2),
	quiet_from: z.string().max(5).default('17:30'),
	quiet_until: z.string().max(5).default('08:00'),
	weekends_off: z.boolean().default(true),
	push_when_closed: z.boolean().default(true),
	meeting_prep_lead_min: z.union([z.literal(5), z.literal(10), z.literal(15), z.literal(30)]).default(15),
	internal_meetings: z.boolean().default(false),
	kinds: z
		.object({
			meeting_prep: z.boolean().default(true),
			client_followup: z.boolean().default(true),
			outstanding_work: z.boolean().default(true),
			review_time: z.boolean().default(true),
			promise_due: z.boolean().default(true),
			connect_invite: z.boolean().default(true),
			agent_task: z.boolean().default(true),
			waiting_on_you: z.boolean().default(true),
			waiting_on_them: z.boolean().default(true),
			wrap_up: z.boolean().default(true),
		})
		.default({
			meeting_prep: true,
			client_followup: true,
			outstanding_work: true,
			review_time: true,
			promise_due: true,
			connect_invite: true,
			agent_task: true,
			waiting_on_you: true,
			waiting_on_them: true,
			wrap_up: true,
		}),
	/** Kinds paused after 3 ignores in a row: kind -> ISO time they resume. */
	paused_until: z.record(z.string().max(40), z.string().max(40)).default({}),
	/** list_only: nothing pops up (meeting prep included); everything still goes into the From Konvo list. */
	popups: z.enum(CORNER_POPUP_MODES).default('on'),
	/**
	 * Local time "HH:MM" before which nothing pops up (meeting prep included); what is ready earlier
	 * waits in the From Konvo list. Absent: no such limit (quiet hours still apply).
	 */
	popups_from: z.string().max(5).optional(),
	/** Starting points in Mail, Tasks and Calendar. */
	starters_enabled: z.boolean().default(true),
	starter_labels: z.enum(['always', 'icons_after_use']).default('icons_after_use'),
});
export type CornerSettings = z.infer<typeof CornerSettingsSchema>;

/** The answers to "Am I getting in your way?" (POST /konvo/corner/ask-change). */
export const ASK_CHANGE_CHOICES = [
	'fewer', // at most one suggestion a day
	'clients_meetings', // only meeting prep, waiting on you/them and promises
	'not_mornings', // nothing pops up before 13:00
	'no_agent_chats', // no agent chat questions
	'wrong_people', // mute the people of the last waiting pop-ups that were closed
	'list_only', // nothing pops up; everything goes into the From Konvo list
] as const;
export type AskChangeChoice = (typeof ASK_CHANGE_CHOICES)[number];

/** POST /konvo/corner/ask-change. No choices (and no text) is "Keep as is": the ask is only cleared. */
export const AskChangeRequestSchema = z.object({
	choices: z.array(z.enum(ASK_CHANGE_CHOICES)).max(ASK_CHANGE_CHOICES.length),
	/** "Or tell me in your own words…": kept with the answer and passed on as feedback. */
	text: z.string().max(500).optional(),
});
export type AskChangeRequest = z.infer<typeof AskChangeRequestSchema>;

/** One row of "Got it. From now on:". */
export const AskChangeSummaryRowSchema = z.object({
	icon: z.enum(READY_LINE_ICONS),
	title: z.string().max(160),
	detail: z.string().max(200).optional(),
});
export type AskChangeSummaryRow = z.infer<typeof AskChangeSummaryRowSchema>;

export const AskChangeResponseSchema = z.object({
	settings: CornerSettingsSchema,
	summary: z.array(AskChangeSummaryRowSchema).max(8),
	/** POST /konvo/corner/ask-change/undo with it puts settings and mutes back. Null for "Keep as is". */
	undo_token: z.string().max(80).nullable(),
});
export type AskChangeResponse = z.infer<typeof AskChangeResponseSchema>;

/** POST /konvo/corner/ask-change/undo */
export const AskChangeUndoRequestSchema = z.object({undo_token: z.string().min(1).max(80)});
export type AskChangeUndoRequest = z.infer<typeof AskChangeUndoRequestSchema>;

/** GET/PUT /konvo/insights/people/:userId/corner (staff): pause someone's pop-ups or change their daily limit. */
export const StaffCornerLimitsSchema = z.object({
	enabled: z.boolean().optional(),
	per_day: z.union([z.literal(1), z.literal(2), z.literal(4)]).optional(),
});
export type StaffCornerLimits = z.infer<typeof StaffCornerLimitsSchema>;

/** GET /konvo/corner/recent — Settings → Recent suggestions. */
export const CornerRecentSchema = z.object({
	items: z.array(
		z.object({
			id: z.string(),
			kind: z.enum(CORNER_KINDS),
			title: z.string(),
			at: z.string(),
			status: z.enum(CORNER_STATUSES),
		}),
	),
});
export type CornerRecent = z.infer<typeof CornerRecentSchema>;

// ---------------------------------------------------------------------------
// Getting started (Home card and DM starter cards)
// ---------------------------------------------------------------------------

export const GettingStartedSchema = z.object({
	stage: z.enum(ASSISTANT_STAGES),
	connected: z.boolean(),
	/** Home "Get started with your assistant" card (connected, fewer than 3 done, within 14 days, not dismissed). */
	show_home_card: z.boolean(),
	/** DM starter cards (first open after connecting, or 3+ working days without a turn, once a week). */
	show_dm_starters: z.boolean(),
	items: z.array(
		z.object({
			id: z.string().max(40),
			starter: z.enum(STARTERS),
			title: z.string().max(120),
			reason: z.string().max(160),
			request: z.string().max(1000),
			context: z.record(z.string().max(40), z.string().max(400)).optional(),
			done: z.boolean(),
			/** The best one right now gets the filled button. */
			primary: z.boolean().default(false),
		}),
	),
});
export type GettingStarted = z.infer<typeof GettingStartedSchema>;

// ---------------------------------------------------------------------------
// Nudges from Konvo (a card in the Konvo DM)
// ---------------------------------------------------------------------------

/** Embed provider name of the nudge card; its JSON sits in fields named `nudge:0..n`. */
export const NUDGE_CARD_PROVIDER = 'Konvo:nudge';
export const NUDGE_CARD_FIELD_PREFIX = 'nudge:';

/**
 * Embed provider of the connect card Konvo posts when someone writes in the Konvo DM before
 * connecting an assistant. No fields: older apps show its title and description as a plain card.
 */
export const ASSISTANT_CONNECT_PROVIDER = 'Konvo:connect';

export const NudgeCardSchema = z.object({
	version: z.literal(1),
	nudge_id: z.string().max(40),
	/** Show the Connect button (hidden once connected). */
	connect: z.boolean(),
	tries: z
		.array(
			z.object({
				starter: z.enum(STARTERS),
				title: z.string().max(120),
				reason: z.string().max(160),
				request: z.string().max(1000),
				context: z.record(z.string().max(40), z.string().max(400)).optional(),
			}),
		)
		.max(3),
	/** Optional 10-minute setup call: the staff member's free slots. */
	call_slots: z
		.array(z.object({start: z.string(), end: z.string()}))
		.max(4)
		.optional(),
	/** Who the setup call is with (the staff member who asked for the nudge). */
	call_with: z.object({name: z.string().max(120), address: z.string().max(320)}).optional(),
});
export type NudgeCard = z.infer<typeof NudgeCardSchema>;

/** POST /konvo/insights/nudges (staff). */
export const SendNudgeSchema = z.object({
	user_ids: z.array(z.string().max(40)).min(1).max(50),
	/** The message in Konvo's voice; empty means the default text per person. */
	text: z.string().max(1500).optional(),
	include_connect: z.boolean().default(true),
	include_tries: z.boolean().default(true),
	offer_call: z.boolean().default(false),
	/** Also open the staff member's own DM with each person afterwards (client only). */
	open_my_dm: z.boolean().default(false),
});
export type SendNudge = z.infer<typeof SendNudgeSchema>;

/**
 * Why a nudge was not sent. skipped: nudged_recently | opted_out | not_found | not_enabled (the new
 * assistant experience is not switched on for them yet, see the rollout below); failed: error;
 * held: held_for_update (none of their apps can show the card yet: it is kept and sent by the
 * server when one of their apps next reports a build that can, see ClientVersion.ts).
 */
export const NUDGE_RESULT_REASONS = [
	'nudged_recently',
	'opted_out',
	'not_found',
	'not_enabled',
	'error',
	'held_for_update',
] as const;
export type NudgeResultReason = (typeof NUDGE_RESULT_REASONS)[number];

/**
 * POST /konvo/insights/nudges response: one line per person (409 when every person was skipped).
 * `held` nudges count toward the week's one nudge and go out once the person's app is updated.
 */
export const SendNudgeResultSchema = z.object({
	results: z.array(
		z.object({
			user_id: z.string(),
			status: z.enum(['sent', 'held', 'skipped', 'failed']),
			reason: z.enum(NUDGE_RESULT_REASONS).nullable(),
			nudge_id: z.string().nullable(),
		}),
	),
});
export type SendNudgeResult = z.infer<typeof SendNudgeResultSchema>;

/** Field values stay well under the 1,024-character embed field limit. */
export const NUDGE_CARD_CHUNK_SIZE = 900;

export function encodeNudgeCard(card: NudgeCard): Array<string> {
	const json = JSON.stringify(NudgeCardSchema.parse(card));
	const chunks: Array<string> = [];
	for (let offset = 0; offset < json.length; offset += NUDGE_CARD_CHUNK_SIZE) {
		chunks.push(json.slice(offset, offset + NUDGE_CARD_CHUNK_SIZE));
	}
	return chunks;
}

export function decodeNudgeCardFields(
	fields: ReadonlyArray<{name?: string | null; value?: string | null}> | null | undefined,
): NudgeCard | null {
	const chunks = (fields ?? [])
		.flatMap((field) => {
			if (!field.name?.startsWith(NUDGE_CARD_FIELD_PREFIX) || typeof field.value !== 'string') return [];
			const index = Number(field.name.slice(NUDGE_CARD_FIELD_PREFIX.length));
			return Number.isInteger(index) && index >= 0 ? [{index, value: field.value}] : [];
		})
		.sort((left, right) => left.index - right.index);
	if (chunks.length === 0 || chunks.some((chunk, index) => chunk.index !== index)) return null;
	try {
		return NudgeCardSchema.parse(JSON.parse(chunks.map((chunk) => chunk.value).join('')));
	} catch {
		return null;
	}
}

// ---------------------------------------------------------------------------
// Team insights (staff only)
// ---------------------------------------------------------------------------

export const SETUP_ITEMS = ['microsoft', 'clickup', 'assistant', 'whatsapp', 'desktop', 'phone'] as const;
export type SetupItem = (typeof SETUP_ITEMS)[number];

const PersonRef = z.object({
	user_id: z.string(),
	name: z.string(),
	avatar: z.string().nullable(),
});
export type InsightsPersonRef = z.infer<typeof PersonRef>;

/** GET /konvo/insights/overview?days=7 */
export const InsightsOverviewSchema = z.object({
	days: z.number().int(),
	kpis: z.object({
		people_in_konvo: z.number().int(),
		people_total: z.number().int(),
		median_seconds_per_day: z.number(),
		median_seconds_delta: z.number(),
		assistant_connected: z.number().int(),
		assistant_weekly: z.number().int(),
		assistant_turns: z.number().int(),
		/** Share of the turns from the busiest person (0–1), to show how concentrated use is. */
		assistant_top_share: z.number().min(0).max(1).default(0),
		suggestions_shown: z.number().int(),
		suggestions_used: z.number().int(),
		suggestions_dismissed: z.number().int(),
		nudges_sent: z.number().int(),
		nudges_read: z.number().int(),
		nudges_connect_clicked: z.number().int(),
		nudges_connected: z.number().int(),
	}),
	areas: z.array(
		z.object({area: z.enum(ACTIVITY_AREAS), seconds: z.number(), share: z.number(), people: z.number().int()}),
	),
	findings: z.array(
		z.object({
			id: z.string(),
			icon: z.enum(['mail', 'tasks', 'suggestion', 'chats', 'whatsapp', 'assistant', 'calendar']),
			title: z.string(),
			detail: z.string(),
			source: z.string().nullable(),
			action: z.enum(['plan_suggestion', 'nudge', 'open_suggestions', 'none']),
			user_ids: z.array(z.string()),
		}),
	),
	grid: z.array(
		PersonRef.extend({
			/** Local days in the window (out of `days`) the person used each area. */
			days_by_area: z.record(z.string(), z.number().int()),
			setup: z.record(z.string(), z.boolean()),
			stage: z.enum(ASSISTANT_STAGES),
		}),
	),
});
export type InsightsOverview = z.infer<typeof InsightsOverviewSchema>;

/** GET /konvo/insights/people?days=7 */
export const InsightsPeopleSchema = z.object({
	days: z.number().int(),
	/** The newest release build any app reported in the last two weeks (ClientVersion.ts). */
	client_latest_build: z.string().nullable().default(null),
	stages: z.record(z.string(), z.number().int()),
	starters: z.array(
		z.object({starter: z.enum(STARTERS), shown: z.number().int(), opened: z.number().int(), used: z.number().int()}),
	),
	people: z.array(
		PersonRef.extend({
			stage: z.enum(ASSISTANT_STAGES),
			last_used_at: z.string().nullable(),
			starters_tried: z.number().int(),
			nudged_at: z.string().nullable(),
			invites_shown: z.number().int(),
			/** Which build each of their apps runs (POST /konvo/client/hello). */
			client_devices: z.array(ClientDeviceSchema).default([]),
			/** A nudge is waiting for their app to update (asked for at this time), or null. */
			nudge_held_at: z.string().nullable().default(null),
		}),
	),
});
export type InsightsPeople = z.infer<typeof InsightsPeopleSchema>;

export const TimelineMarkerSchema = z.object({
	id: z.string(),
	/** Minutes after local midnight (the person's; on the Schedule, the viewer's). */
	minute: z.number(),
	kind: z.enum(['suggestion', 'meeting_prep', 'nudge', 'starter', 'home', 'panel']),
	outcome: z.enum(['used', 'later', 'dismissed', 'timed_out', 'held', 'planned', 'shown', 'none']),
	label: z.string(),
	/** Planned markers: why it will appear and what would cancel it. */
	reason: z.string().optional(),
	cancel_if: z.string().optional(),
	suggestion_id: z.string().optional(),
});
export type TimelineMarker = z.infer<typeof TimelineMarkerSchema>;

/** GET /konvo/insights/people/:id?days=7 */
export const InsightsPersonSchema = z.object({
	person: PersonRef.extend({
		stage: z.enum(ASSISTANT_STAGES),
		devices: z.array(z.enum(DEVICE_KINDS)),
		last_seen_at: z.string().nullable(),
		setup: z.record(z.string(), z.boolean()),
		suggestions_paused: z.boolean(),
		/** Which build each of their apps runs, newest report first. */
		client_devices: z.array(ClientDeviceSchema).default([]),
	}),
	areas: z.array(z.object({area: z.enum(ACTIVITY_AREAS), days: z.number().int(), note: z.string().nullable()})),
	/** One line explaining the pattern (deterministic, not the model). */
	read: z.string().nullable(),
	timeline: z.array(
		z.object({
			date: z.string(),
			label: z.string(),
			/** Time in Konvo: [startMinute, endMinute]. */
			bands: z.array(z.tuple([z.number(), z.number()])),
			markers: z.array(TimelineMarkerSchema),
		}),
	),
	log: z.array(
		z.object({
			at: z.string(),
			event: z.string(),
			where: z.string(),
			detail: z.string(),
			result: z.string(),
			tone: z.enum(['red', 'amber', 'blue', 'neutral', 'green']),
		}),
	),
});
export type InsightsPerson = z.infer<typeof InsightsPersonSchema>;

/**
 * GET /konvo/insights/schedule?date=YYYY-MM-DD. Everything is on the viewer's clock: `date` is a
 * day in the viewer's timezone and every minute (now line, markers) is after the viewer's midnight.
 * Labels, reasons and cancel_if are generic (kind, counts, times), never the person's mail or
 * calendar text.
 */
export const InsightsScheduleSchema = z.object({
	date: z.string(),
	/** Minutes after the viewer's local midnight, or null when the date is not today. */
	now_minute: z.number().nullable(),
	people: z.array(
		PersonRef.extend({
			stage: z.enum(ASSISTANT_STAGES),
			preps_today: z.number().int(),
			suggestions_used: z.number().int(),
			limit: z.number().int(),
			invites_shown: z.number().int(),
			next_invite: z.string().nullable(),
			markers: z.array(TimelineMarkerSchema),
		}),
	),
});
export type InsightsSchedule = z.infer<typeof InsightsScheduleSchema>;

/**
 * How much someone uses Agents (konvo_api agents/AgentsUse.ts): agent work on their machines, and
 * what they did in Konvo against the agent tool itself (the T3 Code app). Counts only, never prompts.
 */
const AgentUseCounts = z.object({
	/** Seconds the agents worked: every finished turn on the person's machines, started anywhere. */
	agent_seconds: z.number(),
	/**
	 * Finished turns: how many a prompt from Konvo started (or went into), and how many no person
	 * started (background work back, CI notices, pull request checks, other agents). The rest were
	 * typed in the T3 Code app. Konvo against the app compares only what people wrote.
	 */
	turns: z.number().int(),
	turns_from_konvo: z.number().int(),
	turns_auto: z.number().int().default(0),
	/** Times an agent stopped for an approval or a question, and how many were answered in Konvo (`answers`). */
	asks: z.number().int().default(0),
	/** Chats with at least one finished turn. */
	chats: z.number().int(),
	/** Done in Konvo: prompts sent (and from a phone), chats started, approvals and questions answered. */
	prompts: z.number().int(),
	prompts_phone: z.number().int(),
	chats_started: z.number().int(),
	answers: z.number().int(),
	/** Seconds with the Agents page open and in front. */
	page_seconds: z.number(),
});
export type AgentUseCounts = z.infer<typeof AgentUseCounts>;

/** GET /konvo/insights/agent-use?days=7 (staff): how much each person uses Agents. */
export const InsightsAgentUseSchema = z.object({
	days: z.number().int(),
	/** The team's local days, oldest first, for the per-day bars. */
	dates: z.array(z.object({date: z.string(), label: z.string()})),
	totals: AgentUseCounts.extend({
		/** People who used Agents in Konvo or whose agents worked, in the range. */
		people_using: z.number().int(),
		/** People with a machine connected, and everyone on the team. */
		people_with_machine: z.number().int(),
		people_total: z.number().int(),
		/** The same range just before, for "vs last week". */
		agent_seconds_before: z.number(),
		prompts_before: z.number().int(),
	}),
	/** Per day of `dates`: agent seconds, and how much of it turns from Konvo and automatic turns. */
	daily: z.array(z.object({agent_seconds: z.number(), konvo_seconds: z.number(), auto_seconds: z.number().default(0)})),
	people: z.array(
		PersonRef.extend({
			...AgentUseCounts.shape,
			has_machine: z.boolean(),
			/** Days (out of `days`) they used Agents in Konvo. */
			days_used: z.number().int(),
			/** Days their agents worked. */
			days_worked: z.number().int(),
			/** Agent seconds per day of `dates`. */
			daily_seconds: z.array(z.number()),
			last_used_at: z.string().nullable(),
		}),
	),
});
export type InsightsAgentUse = z.infer<typeof InsightsAgentUseSchema>;

/** GET /konvo/insights/suggestions?days=7 */
export const InsightsSuggestionsSchema = z.object({
	days: z.number().int(),
	kinds: z.array(
		z.object({
			kind: z.enum(CORNER_KINDS),
			shown: z.number().int(),
			seen: z.number().int(),
			used: z.number().int(),
			later: z.number().int(),
			dismissed: z.number().int(),
			stopped: z.number().int(),
			timed_out: z.number().int(),
			median_ms_to_act: z.number().nullable(),
		}),
	),
	clicks: z.object({
		main: z.number(),
		later: z.number(),
		x: z.number(),
		menu: z.number(),
		none: z.number(),
	}),
	next_five_minutes: z.object({
		stayed: z.number(),
		left: z.number(),
		same_opened: z.number(),
		asked_konvo: z.number(),
	}),
	by_page: z.array(z.object({page: z.string(), shown: z.number().int(), used: z.number().int()})),
	nudges: z.array(
		PersonRef.extend({
			nudge_id: z.string(),
			sent_at: z.string(),
			requested_by: z.string().nullable(),
			automatic: z.boolean(),
			read_at: z.string().nullable(),
			connect_clicked_at: z.string().nullable(),
			connected_at: z.string().nullable(),
			stopped_at_step: z.string().nullable(),
			/** Waiting for the person's app to update; `sent_at` is when it was asked for. */
			held: z.boolean().default(false),
		}),
	),
});
export type InsightsSuggestions = z.infer<typeof InsightsSuggestionsSchema>;

/** GET /konvo/insights/nudges/preview?user_id= (staff): the default text and tries for one person. */
export const NudgePreviewSchema = z.object({
	user: PersonRef.extend({stage: z.enum(ASSISTANT_STAGES), invites_shown: z.number().int()}),
	text: z.string(),
	tries: TeaserSchema.shape.tries,
	nudged_at: z.string().nullable(),
	can_send: z.boolean(),
	/** The new assistant experience is switched on for them (false: can_send is false too). */
	enabled: z.boolean().default(true),
	/**
	 * Their apps' builds. cards_ready false: a nudge sent now is held until one of their apps
	 * reports a build that shows nudge cards (ClientVersion.ts cardsReady). Absent from older servers.
	 */
	client: z
		.object({
			devices: z.array(ClientDeviceSchema),
			cards_ready: z.boolean(),
			min_build: z.string().nullable(),
			/** A nudge already waits for their update (asked for at this time). */
			held_at: z.string().nullable(),
		})
		.optional(),
});
export type NudgePreview = z.infer<typeof NudgePreviewSchema>;

// ---------------------------------------------------------------------------
// Rollout: who gets the new experience (corner, starting points, getting started, nudges)
// ---------------------------------------------------------------------------

/**
 * off: nobody; list: only `user_ids`; everyone: all people. Tracking (activity events) and the
 * staff Team insights page stay on for everyone regardless, so there is a baseline.
 */
export const ENGAGEMENT_ROLLOUT_MODES = ['off', 'list', 'everyone'] as const;
export type EngagementRolloutMode = (typeof ENGAGEMENT_ROLLOUT_MODES)[number];

/** PUT /konvo/insights/rollout (staff): replaces the stored record. */
export const EngagementRolloutSchema = z.object({
	mode: z.enum(ENGAGEMENT_ROLLOUT_MODES),
	user_ids: z.array(z.string().regex(/^\d{1,20}$/)).max(2000),
});
export type EngagementRollout = z.infer<typeof EngagementRolloutSchema>;

/** GET/PUT /konvo/insights/rollout response: the stored record and what applies right now. */
export const EngagementRolloutStateSchema = EngagementRolloutSchema.extend({
	/** KONVO_ENGAGEMENT_ROLLOUT on the server (the emergency switch); it wins over `mode` while set. */
	env_override: z.enum(ENGAGEMENT_ROLLOUT_MODES).nullable(),
	/** env_override ?? mode. */
	effective_mode: z.enum(ENGAGEMENT_ROLLOUT_MODES),
});
export type EngagementRolloutState = z.infer<typeof EngagementRolloutStateSchema>;

/** GET /konvo/engagement/access: whether the signed-in person gets the new experience. */
export const EngagementAccessSchema = z.object({enabled: z.boolean()});
export type EngagementAccess = z.infer<typeof EngagementAccessSchema>;
